import debounce from 'lodash/debounce';

const IS_AAA_LOGGED_KEY = Symbol();
const DEBOUNCE_TIME_IN_MILLISECONDS = 200;

class AAADataService {
  #logPageDataDebounce: (() => void) | null = null;
  #currentState: string | null = null;
  #currentHref: string | null = null;
  #requestId: string | null = null;
  #referrer: string | null = null;

  async init() {
    if (process.env.NODE_ENV === 'development') {
      return;
    }

    const response = await fetch(
      process.env.NEXT_PUBLIC_ANALYTIC_LOG_DOMAIN || '/log/',
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ event_type: 'init' }),
      },
    );

    /** prevent spam in we haven't connected /log */
    if (!response.ok) {
      return;
    }

    this.#requestId = window.crypto?.randomUUID?.() ?? Date.now();
    this.#currentState = history.state;
    this.#referrer = document.referrer || 'Direct';
    this.#currentHref = location.href;
    this.#logPageDataDebounce = debounce(() => {
      this.logPageData();
    }, DEBOUNCE_TIME_IN_MILLISECONDS);
    this.listenForDataLayerChanges();
  }

  logPageData() {
    const urlParams = new URLSearchParams(window.location.search);
    const utmParams: { name: string; value: string }[] = [];
    const postParams = [];

    urlParams.forEach((value, name) => {
      if (name.toLowerCase().startsWith('utm_')) {
        utmParams.push({ name, value });
      }
    });

    const searchterm = urlParams.get('fss') || '';
    if (searchterm != '') {
      postParams.push({ name: 'searchterm', value: searchterm });
    }

    const stateAsJson = JSON.stringify(history.state);
    const currentReferrer = JSON.stringify(this.#currentState);

    if (currentReferrer !== stateAsJson) {
      this.#requestId = window.crypto?.randomUUID?.() ?? Date.now();
      this.#currentState = history.state;
      this.#referrer = this.#currentHref;
    }

    this.#currentHref = location.href;

    const dataLayerUnlogged = window.dataLayer.filter((item: any) => {
      return !(item.isServerSideLogged || item[IS_AAA_LOGGED_KEY]);
    });

    dataLayerUnlogged.forEach((item: any) => (item[IS_AAA_LOGGED_KEY] = true));
    const logData = {
      url: window.location.href,
      get_params: utmParams,
      referrer: this.#referrer,
      post_params: postParams,
      event_type: 'pageview',
      request_id: this.#requestId,
      datalayer: dataLayerUnlogged,
    };

    this.sendLogData(logData);
  }

  async sendLogData(logData?: Record<string, unknown>) {
    try {
      let body;

      try {
        body = JSON.stringify(logData);
      } catch {
        body = '{}';
      }

      const response = await fetch(
        process.env.NEXT_PUBLIC_ANALYTIC_LOG_DOMAIN || '/log/',
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },

          body,
        },
      );

      if (!response.ok) {
        throw new Error('Failed to send log data.');
      }
    } catch (error) {
      console.error('Error sending log data:', error);
    }
  }

  onDataLayerChange() {
    this.#logPageDataDebounce?.();
  }

  listenForDataLayerChanges() {
    const oldPush = window.dataLayer.push;
    window.dataLayer.push = (...args: number[]) => {
      oldPush.apply(window.dataLayer, args);
      this.onDataLayerChange(...(args as []));
    };
  }
}

const _AAADataService = new AAADataService();

export default _AAADataService;
